/**
 * CreditGuardHost — the ONE global surface for AI-credit outages.
 *
 * Mounted once in the shell (DashboardLayout). It listens for the
 * `psn:ai-credits-insufficient` window event that the API client fires on ANY
 * 402/insufficient_ai_credits (lib/api/client.ts) — so no matter which feature
 * ran the AI call (wizard, composer, studio, copilot, account-intelligence,
 * prospection, CRM enrich, email drafting…), the user gets the SAME clear
 * message + a one-click path to refill. It shows a modal (feature that was
 * blocked, credits required vs available, current balance) with a "Recharger"
 * CTA that deep-links to the buy-credits flow, plus a toast for a lightweight
 * heads-up. It also keeps the shared wallet fresh (badge + settings read it).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  AI_CREDITS_CHARGED_EVENT,
  AI_CREDITS_INSUFFICIENT_EVENT,
  rememberBlockedRun,
  type CreditsCharged,
  type NormalizedInsufficientCredits,
} from '@/lib/api/ai-credits';
import { isUnlimitedWallet, requestMoreCredits } from '@/lib/api/credits';
import { Modal, useToast } from '@/ui';
import { AI_CREDITS_UPDATED_EVENT, useCreditsStore } from './useCreditsStore';
import {
  AI_CREDITS_TOPPED_UP_EVENT,
  connectCreditsRealtime,
  isCreditsRealtimeConnected,
} from './creditsRealtime';
import { featureLabel } from './featureLabels';
import { useT, intlLocale } from '@/i18n';

// Realtime pushes (credits:balance/topped_up/low_balance) are the primary
// transport; this poll is only a slow backstop for when the socket is down.
const REFRESH_MS = 90_000;

function fmtCredits(value: number | undefined | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString(intlLocale('fr-FR'), { maximumFractionDigits: 4 });
}

export function CreditGuardHost() {
  const { t } = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const { wallet, refresh } = useCreditsStore();
  const [pending, setPending] = useState<NormalizedInsufficientCredits | null>(null);
  // Avoid a toast storm when several AI calls 402 back-to-back.
  const lastToastAt = useRef(0);
  // Warn once per low-balance episode (proactive nudge — the polling below picks
  // up a low balance without needing the backend's realtime event).
  const lowWarned = useRef(false);

  // Keep the wallet fresh: realtime pushes are primary; the interval is a
  // backstop that only fires while the socket is NOT connected.
  useEffect(() => {
    void refresh();
    const disconnectRealtime = connectCreditsRealtime();
    const id = window.setInterval(() => {
      if (!isCreditsRealtimeConnected()) void refresh();
    }, REFRESH_MS);
    const onUpdated = () => void refresh({ force: true });
    // A backend-confirmed top-up: clear the out-of-credits dialog (the paused
    // run auto-resumes server-side) and re-fetch the wallet.
    const onToppedUp = () => {
      setPending(null);
      void refresh({ force: true });
      toast.success(t('shell.features.creditsCreditGuardHost.creditsToppedUpYourPausedTasks'), 6000);
    };
    window.addEventListener(AI_CREDITS_UPDATED_EVENT, onUpdated);
    window.addEventListener(AI_CREDITS_TOPPED_UP_EVENT, onToppedUp);
    return () => {
      window.clearInterval(id);
      disconnectRealtime();
      window.removeEventListener(AI_CREDITS_UPDATED_EVENT, onUpdated);
      window.removeEventListener(AI_CREDITS_TOPPED_UP_EVENT, onToppedUp);
    };
  }, [refresh, toast]);

  // Success path: any response that reports credits spent updates the visible
  // balance at once and toasts « X crédits utilisés · solde Y ». A reconciling
  // refresh follows (authoritative wallet, cycle usage %) so a reload agrees.
  useEffect(() => {
    const onCharged = (e: Event) => {
      const d = (e as CustomEvent<CreditsCharged>).detail;
      if (!d || !(d.charged > 0)) return;
      if (d.balanceAfter != null) useCreditsStore.getState().applyBalanceAfter(d.balanceAfter);
      const charged = fmtCredits(d.charged);
      const msg =
        d.balanceAfter != null
          ? t(d.charged <= 1 ? 'shell.features.creditsUsage.usedToastOne' : 'shell.features.creditsUsage.usedToast', {
              charged,
              balance: fmtCredits(d.balanceAfter),
            })
          : t('shell.features.creditsUsage.usedToastNoBalance', { charged });
      toast.success(msg, 5000);
      void refresh({ force: true });
    };
    window.addEventListener(AI_CREDITS_CHARGED_EVENT, onCharged);
    return () => window.removeEventListener(AI_CREDITS_CHARGED_EVENT, onCharged);
  }, [refresh, toast, t]);

  // The global 402 listener: open the refill modal + a throttled toast, and
  // re-fetch the wallet (the reservation may have moved the numbers).
  useEffect(() => {
    const onInsufficient = (e: Event) => {
      const detail = (e as CustomEvent<NormalizedInsufficientCredits>).detail ?? {};
      setPending(detail);
      // L-resume: remember the blocked run so the settings page can offer
      // "Recharger et continuer" for THIS run after the Stripe top-up.
      rememberBlockedRun(detail.blockedRunId);
      void refresh({ force: true });
      const now = Date.now();
      if (now - lastToastAt.current > 4000) {
        lastToastAt.current = now;
        toast.error(
          detail.detail ||
            (detail.code === 'user_budget_exceeded'
              ? t('shell.features.creditsCreditGuardHost.personalAiCapReachedContactAn')
              : detail.code === 'engine_unavailable'
                ? t('shell.features.creditsCreditGuardHost.aiCreditsTemporarilyUnavailableTryAgain')
                : t('shell.features.creditsCreditGuardHost.insufficientAiCreditsTopUpYour')),
          6000,
        );
      }
    };
    window.addEventListener(AI_CREDITS_INSUFFICIENT_EVENT, onInsufficient);
    return () => window.removeEventListener(AI_CREDITS_INSUFFICIENT_EVENT, onInsufficient);
  }, [refresh, toast]);

  // Proactive low-balance nudge: one toast when the wallet crosses into "low"
  // (available at/under its threshold); re-arms once it's topped back up.
  useEffect(() => {
    if (!wallet) return;
    // Unlimited (superadmin) wallets are never "low" — no nudge.
    if (isUnlimitedWallet(wallet)) {
      lowWarned.current = false;
      return;
    }
    const low = (wallet.available ?? 0) <= (wallet.low_balance_threshold ?? 0);
    if (low && !lowWarned.current) {
      lowWarned.current = true;
      toast.info(t('shell.features.creditsCreditGuardHost.lowAiCreditBalanceConsiderTopping'), 7000);
    } else if (!low) {
      lowWarned.current = false;
    }
  }, [wallet, toast]);

  if (!pending) return null;

  const available = pending.available ?? wallet?.available ?? 0;
  const required = pending.required ?? 0;
  const missing = Math.max(required - available, 0);
  const resumable = Boolean(pending.blockedRunId);
  const budgetCapped = pending.code === 'user_budget_exceeded';
  const engineDown = pending.code === 'engine_unavailable';
  const canTopUp = !budgetCapped && !engineDown;

  const goRefill = () => {
    setPending(null);
    // ?resume=1 nudges the settings page to offer resuming the blocked run
    // (which it reads from localStorage) once the top-up completes.
    navigate(`/settings/credits?buy=1${resumable ? '&resume=1' : ''}`);
  };

  const goCoupon = () => {
    setPending(null);
    // Vouchers are redeemed on the referral page (« Utiliser un bon »).
    navigate(`/settings/referral?coupon=1${resumable ? '&resume=1' : ''}`);
  };

  const askAdmin = () => {
    void requestMoreCredits({ note: pending?.feature ? featureLabel(pending.feature) : undefined })
      .then(() => toast.success(t('shell.features.creditsCreditGuardHost.requestSentToAnAdministrator'), 5000))
      .catch(() => toast.error(t('shell.features.creditsCreditGuardHost.couldNotSendTheRequestRight'), 5000));
    setPending(null);
  };

  return (
    <Modal
      title={
        budgetCapped
          ? t('shell.features.creditsCreditGuardHost.personalAiCapReached')
          : engineDown
            ? t('shell.features.creditsCreditGuardHost.aiCreditsTemporarilyUnavailable')
            : t('shell.features.creditsCreditGuardHost.insufficientAiCredits')
      }
      size="md"
      onClose={() => setPending(null)}
      footer={
        <>
          <button
            type="button"
            onClick={() => setPending(null)}
            className="rounded-token border border-border bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-ring"
          >{t('shell.features.creditsCreditGuardHost.later')}</button>
          <button
            type="button"
            onClick={askAdmin}
            className="rounded-token border border-border bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-ring"
          >{t('shell.features.creditsCreditGuardHost.askAnAdmin')}</button>
          {canTopUp && (
          <button
            type="button"
            onClick={goCoupon}
            className="rounded-token border border-border bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-ring"
          >{t('shell.features.creditsCreditGuardHost.iHaveACoupon')}</button>
          )}
          {canTopUp && (
          <button
            type="button"
            onClick={goRefill}
            className="rounded-token bg-gradient-to-br from-primary to-accent px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-elevation-1 transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface"
          >
            {resumable ? t('shell.features.creditsCreditGuardHost.topUpAndContinue') : t('shell.features.creditsCreditGuardHost.topUpMyCredits')}
          </button>
          )}
        </>
      }
    >
      <div className="space-y-3 text-sm" data-testid="insufficient-credits-dialog">
        <p className="text-foreground">
          {pending.detail || t('shell.features.creditsCreditGuardHost.yourAiCreditBalanceDoesNot')}
        </p>
        <p className="text-muted">
          {budgetCapped ? (
            <>{t('shell.features.creditsCreditGuardHost.youHaveReachedYourPersonalMonthly')}{typeof pending.cap === 'number' ? t('shell.features.creditsCreditGuardHost.vCr', { v: fmtCredits(pending.cap) }) : ''}{t('shell.features.creditsCreditGuardHost.theOrganizationSWalletStillHas')}</>
          ) : engineDown ? (
            t('shell.features.creditsCreditGuardHost.theBalanceCouldNotBeVerified')
          ) : (
            <>{t('shell.features.creditsCreditGuardHost.toContinue')}{' '}
              <span className="font-medium text-foreground">
                {pending.feature ? featureLabel(pending.feature) : t('shell.features.creditsCreditGuardHost.thisAiAction')}
              </span>{t('shell.features.creditsCreditGuardHost.youNeedToTopUpYour')}</>
          )}
        </p>
        {canTopUp && (
        <div className="grid grid-cols-3 gap-2">
          <Stat label={t('shell.features.creditsCreditGuardHost.required')} value={`${fmtCredits(required)} cr`} tone="warning" />
          <Stat label={t('shell.features.creditsCreditGuardHost.available')} value={`${fmtCredits(available)} cr`} tone="danger" />
          <Stat label={t('shell.features.creditsCreditGuardHost.missing')} value={`${fmtCredits(missing)} cr`} tone="primary" />
        </div>
        )}
        <p className="text-xs text-muted">{t('shell.features.creditsCreditGuardHost.noCreditWasDebitedTheAi')}{!canTopUp
            ? ''
            : resumable
              ? t('shell.features.creditsCreditGuardHost.afterTheTopUpYourAction')
              : t('shell.features.creditsCreditGuardHost.topUpToTryAgain')}
        </p>
      </div>
    </Modal>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: 'warning' | 'danger' | 'primary' }) {
  const toneClass =
    tone === 'danger'
      ? 'text-danger'
      : tone === 'warning'
        ? 'text-warning'
        : 'text-primary';
  return (
    <div className="rounded-token border border-border bg-surface-2/40 px-2 py-2 text-center">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className={`mt-0.5 text-sm font-bold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}
